import { useDebounce } from '@chegg-wt/core/hooks/functions'
import { Option } from '@chegg-wt/core/types/core'
import React, { useEffect, useState } from 'react'

import Select, { Props as SelectProps } from '../Select'
import * as Styled from './styled'

interface Props extends SelectProps {
  /** optional callback when the filter is changed */
  onFilter?: (filter: string) => void
}

const FilterSelect: React.FC<Props> = ({
  defaultOption,
  onClose,
  onFilter,
  onScrollToBottom,
  onSelect,
  options,
  trigger,
  dropdownAlignment,
  type,
  renderOptionAsLink = false,
  isDropdownVisuallyHiddenOnInitialRender = false,
}) => {
  // contents of the filter bar
  const [filter, setFilter] = useState('')
  // the filtered options displayed by the dropdown
  const [currentOptions, setCurrentOptions] = useState(options)

  // filter the options
  const find = (opts: Option[], f: string) =>
    opts.filter((i) => i.name.toLowerCase().includes(f.toLowerCase()))

  // set currentOptions when options change,
  // filtering them if the filter is set and onFilter isn't defined
  useEffect(() => {
    const opts = filter && !onFilter ? find(options, filter) : options
    setCurrentOptions(opts)
  }, [options])

  // filter options to searchbar input
  const debouncedOnFilter = useDebounce(onFilter, 500)
  const handleFilter = ({ target: { value } }) => {
    setFilter(value)
    return onFilter ? debouncedOnFilter(value) : setCurrentOptions(find(options, value))
  }

  // reset the filter when closed
  const reset = () => {
    if (onClose) onClose()
    setFilter('')
    if (!onFilter) setCurrentOptions(options)
  }

  return (
    <Select
      {...{
        defaultOption,
        options: currentOptions,
        onClose: reset,
        onSelect,
        onScrollToBottom,
        trigger,
        dropdownAlignment,
        type,
        renderOptionAsLink,
        isDropdownVisuallyHiddenOnInitialRender,
      }}
    >
      <Styled.SearchBarWrapper role="combobox" aria-expanded="true">
        <Styled.SearchIcon />
        <Styled.SearchBar
          autoFocus
          value={filter}
          onChange={handleFilter}
          placeholder="Search"
          type="text"
          data-test="filter-bar"
          aria-autocomplete="list"
          aria-controls="listbox"
          aria-label={`Search ${type}`}
        />
      </Styled.SearchBarWrapper>
    </Select>
  )
}

export default FilterSelect
